import React, { useMemo } from 'react';
import { BufferMonthData, TimeGranularity } from './StrategiesUtils';
import { dynamicBufferLinePlugin } from './BufferLinePlugin';
import { IMassUnitObject } from '../../../sp-types/massUnitType';
import { StrategyChart, DatasetConfig } from './StrategyChart';
import { useUser } from '../../../hooks/useUser';
import { Dialog, DialogTitle, DialogContent, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import { Code, ContentCopy } from '@mui/icons-material';
import { ChartControls } from './ChartControls';
import { onHandColorPlugin } from './onHandColorPlugin';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';
import { Account } from '../../../sp-types/accountTypes';

interface Props {
  buffer: number | { baseAmount: number; lookaheadMonths: number; safetyPercentage: number };
  bufferData: BufferMonthData[];
  matPrice: number;
  unit: IMassUnitObject;
  onTimeChange: (_time: TimeGranularity) => void;
  onMonthSelect: (_index: number) => void;
  selectedIndex?: number;
  materialName: string;
  formulaId: string;
  account: Account;
  terminals?: number[] | null;
}

// Transform buffer data to include calculated fields
interface BufferChartData extends BufferMonthData {
  prevStorage: number;
  expectedDemandStacked: number;
  fullDemand: number;
}

export const BufferManagement: React.FC<Props> = ({
  buffer,
  bufferData,
  matPrice,
  unit,
  onTimeChange,
  onMonthSelect,
  selectedIndex,
  materialName,
  formulaId,
  account,
  terminals,
}) => {
  const { data: user } = useUser();
  const [showCalcModal, setShowCalcModal] = React.useState(false);
  const [selectedMonthIndex, setSelectedMonthIndex] = React.useState<number>(0);
  const baseCurrency = useBaseCurrency();
  const { data: formulasPricesData } = useFormulasPrices();

  const terminalCount = React.useMemo(
    () => (terminals || []).length,
    [terminals],
  );

  // Extract numeric buffer value for tooltip (use base amount if config object)
  const bufferValue = typeof buffer === 'number' ? buffer : (buffer?.baseAmount || 0);

  // Transform data for the chart
  const chartData = useMemo<BufferChartData[]>(() => {
    if (bufferData.length === 0) return [];

    // Add on-hand storage as first entry (like Dashboard supply card)
    const onHandStorage = bufferData[0]?.storage || 0;
    const onHandEntry: BufferChartData = {
      month: 'On-Hand',
      storage: onHandStorage,
      prevStorage: 0,
      po: 0,
      expectedDemand: 0,
      buffer: 0,
      expectedDemandStacked: 0,
      fullDemand: 0,
    };

    // Transform regular data points
    const transformedData = bufferData.map((item, index) => {
      // Get previous month's storage (on-hand for first month, then previous month's result)
      const prevStorage = index === 0 ? onHandStorage : bufferData[index - 1].storage;

      return {
        ...item,
        prevStorage,
        expectedDemandStacked: item.expectedDemand + item.buffer - Math.max(0, item.storage + item.po),
        fullDemand: item.expectedDemand + item.buffer,
      };
    });

    return [onHandEntry, ...transformedData];
  }, [bufferData]);

  // Generate debug calculation text
  const calcDebugText = React.useMemo(() => {
    const lines: string[] = [];
    const priceInfo = formulasPricesData?.pricesMap?.[formulaId];

    lines.push('BUFFER STOCK CALCULATION DEBUG');
    lines.push('='.repeat(50));
    lines.push('');
    lines.push(`Formula: ${materialName} (${formulaId})`);
    lines.push(`Buffer Amount: ${bufferValue} MT`);
    lines.push('');

    // Add on-hand storage info
    if (bufferData.length > 0) {
      const onHandStorage = bufferData[0]?.storage || 0;
      lines.push('INITIAL STATE');
      lines.push('='.repeat(50));
      lines.push(`On-Hand Storage: ${onHandStorage.toLocaleString()} MT`);
      lines.push('');
    }

    lines.push('PRICING INFORMATION');
    lines.push('='.repeat(50));
    const unitName = unit?.displayName || 'MT';
    if (priceInfo) {
      if (priceInfo.originalCurrency && priceInfo.originalCurrency !== baseCurrency) {
        lines.push(`Original spot price: ${priceInfo.originalPrice?.toLocaleString()} ${priceInfo.originalCurrency}/${unitName}`);
        const fxRate = priceInfo.price / (priceInfo.originalPrice || priceInfo.price);
        lines.push(`FX rate: 1 ${baseCurrency} = ${(1 / fxRate).toFixed(4)} ${priceInfo.originalCurrency}`);
        lines.push(`Converted price: ${matPrice.toLocaleString()} ${baseCurrency}/${unitName}`);
      } else {
        lines.push(`Spot price: ${matPrice.toLocaleString()} ${baseCurrency}/${unitName}`);
      }
      if (formulasPricesData.asOfDate) {
        lines.push(`As of: ${new Date(formulasPricesData.asOfDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
      }
    } else {
      lines.push(`Price: ${matPrice.toLocaleString()} ${baseCurrency}/${unitName} (no spot price data)`);
    }
    lines.push('');
    lines.push('='.repeat(50));
    lines.push('');
    lines.push('MONTHLY BREAKDOWN (QUANTITY MODE)');
    lines.push('='.repeat(50));
    lines.push('');

    let totalPO = 0;
    let totalDemand = 0;

    bufferData.forEach((month, index) => {
      totalPO += month.po;
      totalDemand += month.expectedDemand;

      // Calculate final storage for this month
      const finalStorage = month.storage + month.po - month.expectedDemand;

      lines.push(`${month.month}:`);
      lines.push(`  Initial Storage: ${month.storage.toLocaleString()} MT`);
      lines.push(`  Supply (PO's): ${month.po.toLocaleString()} MT`);
      lines.push(`  Expected Demand (MRP): ${month.expectedDemand.toLocaleString()} MT`);
      lines.push(`  Calculation: ${month.storage.toLocaleString()} + ${month.po.toLocaleString()} - ${month.expectedDemand.toLocaleString()} = ${finalStorage.toLocaleString()} MT`);
      lines.push(`  Final Storage: ${finalStorage.toLocaleString()} MT`);
      lines.push(`  Buffer (protected): ${bufferValue.toLocaleString()} MT`);
      lines.push('');
    });

    const totalStorage = bufferData.reduce((sum, d) => sum + d.storage, 0);

    lines.push(`Total Storage: ${totalStorage.toLocaleString()} MT`);
    lines.push(`Total PO's: ${totalPO.toLocaleString()} MT`);
    lines.push(`Total Expected Demand: ${totalDemand.toLocaleString()} MT`);
    lines.push('');
    lines.push('='.repeat(50));
    lines.push('');
    lines.push('VALUE MODE');
    lines.push('='.repeat(50));
    lines.push('');
    lines.push(`Price per MT: $${matPrice.toLocaleString()}`);
    lines.push('');

    bufferData.forEach((d) => {
      lines.push(`${d.month}:`);
      lines.push(`  Storage: ${d.storage.toLocaleString()} MT × $${matPrice} = $${(d.storage * matPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      lines.push(`  PO's: ${d.po.toLocaleString()} MT × $${matPrice} = $${(d.po * matPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      lines.push(`  Expected Demand: ${d.expectedDemand.toLocaleString()} MT × $${matPrice} = $${(d.expectedDemand * matPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      lines.push('');
    });

    lines.push(`Total Storage Value: $${(totalStorage * matPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    lines.push(`Total PO Value: $${(totalPO * matPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    lines.push(`Total Demand Value: $${(totalDemand * matPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

    return lines.join('\n');
  }, [bufferData, materialName, formulaId, buffer, matPrice]);

  const datasets: DatasetConfig[] = [
    {
      label: 'Storage',
      dataKey: 'storage',
      backgroundColor: '#1E3A8A', // Dark blue from screenshot
      borderColor: '#1E3A8A',
      borderRadius: 4,
      type: 'bar',
    },
    {
      label: "PO's",
      dataKey: 'po',
      backgroundColor: '#3B82F6', // Light blue from screenshot
      borderColor: '#3B82F6',
      borderRadius: 4,
      type: 'bar',
    },
    {
      label: 'Expected Demand (MRP)',
      dataKey: 'expectedDemandStacked',
      backgroundColor: '#D1D5DB', // Light grey
      borderColor: '#D1D5DB',
      borderRadius: 4,
      type: 'bar',
      residualOf: ['storage', 'po'],
      fullValueKey: 'fullDemand',
    },
  ];

  // Store buffer values in a ref so the plugin always reads current values on each draw.
  // Chart.js doesn't re-register plugins after chart creation, so a ref-based getter
  // is the only reliable way to keep the dashed line in sync with param changes.
  const bufferValuesRef = React.useRef<number[]>([]);
  const multiplierRef = React.useRef(1);

  const pluginsFactory = useMemo(
    () => (isQuantity: boolean, matPrice: number) => {
      multiplierRef.current = isQuantity ? 1 : matPrice;
      bufferValuesRef.current = [0, ...bufferData.map(d => (d.buffer || 0) * multiplierRef.current)];

      return [
        dynamicBufferLinePlugin(() => bufferValuesRef.current),
        onHandColorPlugin
      ];
    },
    [], // stable — plugin reads from refs
  );

  // Keep ref in sync when bufferData or buffer prop changes
  React.useEffect(() => {
    bufferValuesRef.current = [0, ...bufferData.map(d => (d.buffer || 0) * multiplierRef.current)];
  }, [bufferData, buffer]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col flex-1">
        <StrategyChart
          data={chartData}
          matPrice={matPrice}
          unit={unit}
          onTimeChange={onTimeChange}
          datasets={datasets}
          pluginsFactory={pluginsFactory}
          stacked={true}
          extraParams={{ buffer: bufferValue, bufferRows: bufferData }}
          account={account}
          formulaId={formulaId}
          selectedIndex={selectedIndex != null ? selectedIndex + 1 : undefined}
          onBarClick={(index) => {
            // Subtract 1 to account for on-hand bar at index 0
            // So clicking Jan26 (index 1) shows bufferData[0] (Jan26 data)
            const adjustedIndex = Math.max(0, index - 1);
            console.log('[BufferChart Click]', {
              clickedIndex: index,
              adjustedIndex,
              clickedMonth: chartData[index]?.month,
              selectedMonth: bufferData[adjustedIndex]?.month,
              chartDataLength: chartData.length,
              bufferDataLength: bufferData.length
            });
            onMonthSelect(adjustedIndex);
          }}
          renderControls={({ isQuantity, setIsQuantity, timeGranularity, setTimeGranularity, viewMonths, setViewMonths }) => (
            <ChartControls
              title={`${materialName} - Buffer stock management`}
              isQuantity={isQuantity}
              setIsQuantity={setIsQuantity}
              timeGranularity={timeGranularity}
              setTimeGranularity={setTimeGranularity}
              viewMonths={viewMonths}
              setViewMonths={setViewMonths}
              granularityOptions={terminalCount > 0 && terminalCount < 12 ? ['Month', 'Quarter', 'Terminal', 'Yearly', 'Full'] : ['Month', 'Quarter', 'Yearly', 'Full']}
              extraControls={
                user?.isAdmin && timeGranularity !== 'Month' ? (
                  <MuiTooltip title="Show terminal aggregation calculation" arrow placement="top">
                    <button
                      onClick={() => setShowCalcModal(true)}
                      className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      <Code sx={{ fontSize: 17 }} />
                    </button>
                  </MuiTooltip>
                ) : undefined
              }
            />
          )}
        />
      </div>

      {/* Debug modal: show all calculation inputs / outputs */}
      <Dialog
        open={showCalcModal}
        onClose={() => setShowCalcModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Buffer Stock – calculation data</span>
          <IconButton
            size="small"
            onClick={() => {
              try {
                const textToCopy = (window as any).__bufferTerminalDebugLog || calcDebugText;
                if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(textToCopy);
                }
              } catch {
                // ignore clipboard errors
              }
            }}
          >
            <ContentCopy sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <pre
            style={{
              fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              fontSize: '13px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: '1.6',
            }}
          >
            {(window as any).__bufferTerminalDebugLog || calcDebugText}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
};
